import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Clock, Loader2, MapPin, Phone, Send } from "lucide-react";
import { toast } from "sonner";
import { CLINIC, SERVICES } from "@/data/services";
import SocialLinks from "@/components/SocialLinks";

const EASE = [0.16, 1, 0.3, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { nombre: "", telefono: "", servicio: "", mensaje: "" };

const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const onSelect = (e) =>
      setForm((f) => ({ ...f, servicio: e.detail }));
    window.addEventListener("select-service", onSelect);
    return () => window.removeEventListener("select-service", onSelect);
  }, []);

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      toast.success("¡Solicitud enviada!", {
        description: "Te contactaremos muy pronto para confirmar tu cita.",
      });
      setForm(EMPTY);
    } catch {
      toast.error("No se pudo enviar la solicitud", {
        description: `Llámanos al ${CLINIC.phone} y te atendemos de inmediato.`,
      });
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition-[border-color,box-shadow] duration-300 placeholder:text-slate-400 focus:border-red-600 focus:ring-2 focus:ring-red-600/15";

  return (
    <section
      id="contacto"
      data-testid="contact-section"
      className="mx-auto max-w-7xl px-6 py-24 sm:py-32"
    >
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7, ease: EASE }}
        className="mb-16 max-w-2xl"
      >
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-red-700">
          04 — Contacto y citas
        </p>
        <h2 className="mt-4 font-display text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
          Agenda tu cita o envíanos tu consulta
        </h2>
        <p className="mt-4 text-base leading-relaxed text-slate-600">
          Completa el formulario y nuestro equipo te contactará para confirmar
          la fecha y hora de tu atención.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
        <motion.form
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: EASE }}
          onSubmit={submit}
          data-testid="contact-form"
          className="rounded-3xl border border-slate-200/80 bg-white p-8 shadow-sm lg:col-span-7"
        >
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <label
                htmlFor="contact-nombre"
                className="mb-2 block font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500"
              >
                Nombre completo
              </label>
              <input
                id="contact-nombre"
                data-testid="contact-form-nombre-input"
                required
                value={form.nombre}
                onChange={update("nombre")}
                placeholder="Tu nombre"
                className={inputCls}
              />
            </div>
            <div>
              <label
                htmlFor="contact-telefono"
                className="mb-2 block font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500"
              >
                Teléfono
              </label>
              <input
                id="contact-telefono"
                data-testid="contact-form-telefono-input"
                required
                type="tel"
                value={form.telefono}
                onChange={update("telefono")}
                placeholder="Ej. 987 654 321"
                className={inputCls}
              />
            </div>
          </div>
          <div className="mt-5">
            <label
              htmlFor="contact-servicio"
              className="mb-2 block font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500"
            >
              Servicio de interés
            </label>
            <select
              id="contact-servicio"
              data-testid="contact-form-servicio-select"
              required
              value={form.servicio}
              onChange={update("servicio")}
              className={inputCls}
            >
              <option value="" disabled>
                Selecciona un servicio
              </option>
              {SERVICES.map((s) => (
                <option key={s.id} value={s.title}>
                  {s.title}
                </option>
              ))}
              <option value="Consulta general">Consulta general</option>
            </select>
          </div>
          <div className="mt-5">
            <label
              htmlFor="contact-mensaje"
              className="mb-2 block font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500"
            >
              Mensaje
            </label>
            <textarea
              id="contact-mensaje"
              data-testid="contact-form-mensaje-input"
              required
              rows={4}
              value={form.mensaje}
              onChange={update("mensaje")}
              placeholder="Cuéntanos qué necesitas o qué fecha prefieres..."
              className={`${inputCls} resize-none`}
            />
          </div>
          <button
            data-testid="contact-form-submit-button"
            type="submit"
            disabled={sending}
            className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-black py-4 text-sm font-medium text-white transition-[background-color,transform,opacity] duration-300 hover:bg-red-700 active:scale-[0.99] disabled:opacity-60 sm:w-auto sm:px-10"
          >
            {sending ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Send size={16} />
            )}
            {sending ? "Enviando..." : "Enviar solicitud"}
          </button>
        </motion.form>

        <motion.aside
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.12, ease: EASE }}
          data-testid="contact-info-card"
          className="flex flex-col gap-6 lg:col-span-5"
        >
          <div className="rounded-3xl bg-black p-8 text-white">
            <h3 className="font-display text-xl font-semibold">
              Visítanos o llámanos
            </h3>
            <div className="mt-6 space-y-5">
              <a
                data-testid="contact-phone-link"
                href={`tel:${CLINIC.phoneIntl}`}
                className="group flex items-start gap-4"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-600/20 text-red-400 transition-colors group-hover:bg-red-600 group-hover:text-white">
                  <Phone size={16} />
                </span>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                    Teléfono
                  </p>
                  <p className="mt-0.5 text-base font-medium">{CLINIC.phone}</p>
                </div>
              </a>
              <div className="flex items-start gap-4" data-testid="contact-address-block">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-600/20 text-red-400">
                  <MapPin size={16} />
                </span>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                    Dirección
                  </p>
                  <p className="mt-0.5 text-sm leading-relaxed text-slate-200">
                    {CLINIC.address}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4" data-testid="contact-hours-block">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-600/20 text-red-400">
                  <Clock size={16} />
                </span>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                    Horario de atención
                  </p>
                  <p className="mt-0.5 text-sm text-slate-200">{CLINIC.hours}</p>
                  <p className="text-sm text-slate-400">{CLINIC.hoursSunday}</p>
                </div>
              </div>
            </div>
            <a
              data-testid="contact-whatsapp-btn"
              href={CLINIC.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 block rounded-full bg-red-600 py-3.5 text-center text-sm font-medium text-white transition-[background-color,transform] duration-300 hover:bg-red-500 hover:scale-[1.02] active:scale-95"
            >
              Escríbenos por WhatsApp
            </a>
            <div className="mt-6 border-t border-slate-800 pt-6" data-testid="contact-socials">
              <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                Síguenos en redes
              </p>
              <SocialLinks />
            </div>
          </div>

          <div className="overflow-hidden rounded-3xl border border-slate-200/80 shadow-sm">
            <iframe
              data-testid="contact-map"
              title="Ubicación de ECOCORE DX"
              src="https://www.google.com/maps?q=Avenida+Pacasmayo+Urb+Las+Praderas+del+Sol+4ta+Etapa&output=embed"
              className="h-56 w-full border-0"
              loading="lazy"
            />
          </div>
        </motion.aside>
      </div>
    </section>
  );
};

export default Contact;
